export const IDENTITY_CLAIMS = ['sub', 'email']

// One line each, from the user's side: what the claim is and whether
// EducationXR relies on it. Unknown claims read "Published by the identity
// provider." in the popover.
export const CLAIM_DESCRIPTIONS = {
  sub: "The person's permanent ID at this identity provider. EducationXR remembers it, so a changed email address still finds the same account.",
  email: "Email address. Used to find the person's EducationXR account and to route them by domain.",
  preferred_username: 'Usually the sign-in name. It can be changed by the tenant, so it is never used to identify people.',
  name: 'Display name, copied to the profile on first sign-in.',
  given_name: 'First name.',
  family_name: 'Last name.',
  tid: 'The Microsoft tenant the person belongs to. Checked against the tenant value.',
  hd: 'The Google Workspace domain the person belongs to. Checked against the tenant value.',
  groups: 'Group memberships. Microsoft does not list it in discovery, but sends it when the app registration is set to include groups.',
  roles: 'App roles assigned to the person on this app registration.',
  login_hint: 'An opaque hint Microsoft uses to skip "which account?" when signing out.',
  iss: 'Who issued the token. Must match the issuer exactly.',
  aud: 'Who the token is for: our client ID.',
  exp: 'When the token expires.',
  iat: 'When the token was issued.',
  auth_time: 'When the person last entered credentials.',
  nonce: 'Protects against replayed sign-ins.',
  acr: 'How strongly the person authenticated.',
  amr: 'How the person authenticated (password, MFA, ...).',
  ver: 'Token format version.',
  at_hash: 'Integrity check linking the token to its access token.',
  c_hash: 'Integrity check for the sign-in code.'
}

// Claim names come from the identity provider and from staff, so they are
// keys in prototype-less objects: a claim called "constructor" or
// "toString" must not find Object.prototype's.
const own = (obj, key) => Object.prototype.hasOwnProperty.call(obj, key)

export const claimDescription = claim => (own(CLAIM_DESCRIPTIONS, claim) ? CLAIM_DESCRIPTIONS[claim] : 'Published by the identity provider.')

export const claimUsage = (mappings = [], links = []) => {
  const usage = Object.create(null)
  const entry = claim => (usage[claim] = usage[claim] || { mappings: 0, rules: [] })
  mappings.forEach(m => { if (m.claimName) entry(m.claimName).mappings++ })
  links.forEach(l => {
    if (!l.matchData || !l.matchData.claim) return
    const rules = entry(l.matchData.claim).rules
    if (!rules.includes(l.organizationName)) rules.push(l.organizationName)
  })
  return usage
}

const RANK = { identity: 0, used: 1, unadvertised: 1, available: 2 }

export const claimChipModel = (claimsSupported = [], usage = Object.create(null)) => {
  const advertised = new Set(claimsSupported)
  const names = new Set([...claimsSupported, ...IDENTITY_CLAIMS, ...Object.keys(usage)])
  return [...names].map(claim => {
    const used = own(usage, claim) ? usage[claim] : undefined
    const count = used ? used.mappings + used.rules.length : 0
    const kind = IDENTITY_CLAIMS.includes(claim) ? 'identity'
      : used ? (advertised.size && !advertised.has(claim) ? 'unadvertised' : 'used')
        : 'available'
    return { claim, kind, count }
  }).sort((a, b) => RANK[a.kind] - RANK[b.kind] || a.claim.localeCompare(b.claim))
}
